import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  BadgeDollarSign,
  Brain,
  CheckCircle,
  Cpu,
  Eye,
  EyeOff,
  DownloadCloud,
  KeyRound,
  Loader2,
  RefreshCw,
  Save,
  XCircle,
  Zap,
} from 'lucide-react';
import type {
  CatalogoPrecos,
  ConfigIa,
  CustoPorSite,
  DiagnosticoIa,
  ListaModelos,
  ResumoCustoIa,
} from '@nyroh/contracts';

import { api } from '../../lib/api';
import { ErroApiNyroh } from '@nyroh/contracts';

/**
 * ============================================================================
 * INTELIGÊNCIA ARTIFICIAL — a mesma tela da fábrica, no painel
 * ============================================================================
 *
 * Os dois apps precisam dela pelo mesmo motivo e por caminhos diferentes: a
 * fábrica gera site em lote, o painel edita site pelo copiloto do editor. Os
 * dois gastam a MESMA chave e a MESMA cota, e até aqui nenhum dos dois tinha
 * onde ver isso.
 *
 * É um segundo componente, e não um compartilhado, porque não existe pacote de
 * UI entre os apps — eles têm linguagens visuais diferentes (a fábrica é
 * `rounded-sm`/zinc, o painel é `rounded-xl`/gray) e criar um pacote de
 * componentes para uma tela seria a abstração custar mais que a duplicação.
 * O que NÃO está duplicado é o que importa: schema, rotas e regra vivem no
 * contrato, e as duas telas chamam exatamente as mesmas seis rotas.
 */

type Periodo = 'hoje' | '7d' | '30d' | 'todos';

const ROTULO: Record<Periodo, string> = {
  hoje: 'Hoje',
  '7d': '7 dias',
  '30d': '30 dias',
  todos: 'Tudo',
};

const brl = (v: number | null) =>
  v === null
    ? '—'
    : `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

const numero = (v: number) => v.toLocaleString('pt-BR');

function diasDesde(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(`${iso}T00:00:00.000Z`).getTime()) / 86_400_000));
}

function mensagemDeErro(e: unknown): string {
  if (e instanceof ErroApiNyroh) return e.message;
  return e instanceof Error ? e.message : String(e);
}

export const IaControlView: React.FC = () => {
  const [config, setConfig] = useState<ConfigIa | null>(null);
  const [modelos, setModelos] = useState<ListaModelos | null>(null);
  const [resumo, setResumo] = useState<ResumoCustoIa | null>(null);
  const [porSite, setPorSite] = useState<CustoPorSite[]>([]);
  const [periodo, setPeriodo] = useState<Periodo>('30d');

  const [chave, setChave] = useState('');
  const [mostrarChave, setMostrarChave] = useState(false);
  const [diagnostico, setDiagnostico] = useState<DiagnosticoIa | null>(null);

  // O editor de preço. Existe porque a tela manda cadastrar em dois lugares, e
  // sem ele a instrução é um beco: a rota existe e nenhuma interface a chama.
  const [editandoPreco, setEditandoPreco] = useState<string | null>(null);
  const [entradaMi, setEntradaMi] = useState('');
  const [saidaMi, setSaidaMi] = useState('');

  /*
    O catálogo de preços do Google.

    Separado do resto porque é uma consulta EXTERNA e cara (dois GETs no
    catálogo inteiro do Cloud Billing): ela acontece no clique, nunca no
    carregamento da tela. Carregar junto faria abrir a tela de IA custar duas
    idas ao Google que ninguém pediu.
  */
  const [catalogo, setCatalogo] = useState<CatalogoPrecos | null>(null);
  const [buscandoPrecos, setBuscandoPrecos] = useState(false);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [verIgnorados, setVerIgnorados] = useState(false);

  const [carregando, setCarregando] = useState(true);
  const [testando, setTestando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  /**
   * Configuração e catálogo. Separado do custo de propósito.
   *
   * `ia.modelos` é uma consulta AO VIVO ao provedor. Junto com a recarga do
   * custo, trocar o período do relatório disparava uma chamada externa a cada
   * clique — e um soluço do provedor apagava a tabela de modelos da tela sem que
   * ninguém tivesse mexido nela.
   */
  const recarregarConfig = useCallback(async () => {
    const [c, m] = await Promise.all([api.chamar('ia.config', {}), api.chamar('ia.modelos', {})]);
    setConfig(c);
    setModelos(m);
  }, []);

  const recarregarCusto = useCallback(async (p: Periodo) => {
    const [r, s] = await Promise.all([
      api.chamar('ia.resumoCusto', { query: { periodo: p } }),
      api.chamar('ia.custoPorSite', { query: { periodo: p, limite: 30 } }),
    ]);
    setResumo(r);
    setPorSite(s);
  }, []);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    recarregarConfig()
      .catch((e) => vivo && setErro(mensagemDeErro(e)))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [recarregarConfig]);

  useEffect(() => {
    let vivo = true;
    recarregarCusto(periodo).catch((e) => vivo && setErro(mensagemDeErro(e)));
    return () => {
      vivo = false;
    };
  }, [recarregarCusto, periodo]);

  const testar = async () => {
    setTestando(true);
    setErro(null);
    setDiagnostico(null);
    try {
      setDiagnostico(
        await api.chamar('ia.diagnosticar', {
          corpo: chave.trim() ? { chave: chave.trim() } : {},
        }),
      );
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setTestando(false);
    }
  };

  const salvar = async (dados: Parameters<typeof api.chamar<'ia.salvarConfig'>>[1]['corpo']) => {
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await api.chamar('ia.salvarConfig', { corpo: dados });
      setChave('');
      // Só a configuração: o custo não muda por trocar o modelo, e recarregá-lo
      // aqui seria mais uma consulta pesada por clique.
      await recarregarConfig();
      setAviso('Configuração salva.');
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Lê o preço publicado pelo Google. Não grava nada.
   *
   * O que volta é PROPOSTA: o catálogo fala em SKU, com descrição em texto e
   * várias entradas por modelo (tier, região, faixa de contexto), então o
   * casamento SKU → modelo é heurístico. Importar em silêncio trocaria "preço
   * digitado errado" por "preço do SKU errado", que é pior — parece automático
   * e ninguém confere número que veio sozinho.
   */
  const buscarPrecos = async () => {
    setBuscandoPrecos(true);
    setErro(null);
    setAviso(null);
    try {
      const c = await api.chamar('ia.precosDoProvedor', {});
      setCatalogo(c);
      // Já vêm marcados os que mudaram E dá para aplicar. É o caso normal, e
      // marcar um a um oito modelos seria trabalho manual de novo — que é
      // exatamente o que este botão existe para eliminar.
      setMarcados(new Set(c.propostas.filter((p) => p.mudou && p.aplicavel).map((p) => p.modelo)));
      if (c.aviso) setAviso(c.aviso);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setBuscandoPrecos(false);
    }
  };

  const aplicarPrecos = async () => {
    if (marcados.size === 0) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.chamar('ia.aplicarPrecosDoProvedor', {
        corpo: { modelos: [...marcados] },
      });
      await recarregarConfig();
      setCatalogo(null);
      setMarcados(new Set());
      setAviso(
        `${r.aplicados} preço(s) gravados com a data de hoje.` +
          (r.naoAplicados.length
            ? ` ${r.naoAplicados.length} recusado(s): ${r.naoAplicados
                .map((n) => `${n.modelo} (${n.motivo})`)
                .join('; ')}`
            : '') +
          ' O custo já registrado NÃO é recalculado — o preço vale das próximas chamadas em diante.',
      );
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  };

  const salvarPreco = async (modelo: string) => {
    const entrada = parseFloat(entradaMi);
    const saida = parseFloat(saidaMi);
    if (!Number.isFinite(entrada) || !Number.isFinite(saida) || entrada < 0 || saida < 0) {
      setErro('Informe os dois preços em dólar por milhão de tokens, sem sinal negativo.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await api.chamar('ia.salvarPreco', {
        params: { modelo },
        corpo: { usdEntradaMi: entrada, usdSaidaMi: saida, fonte: 'cadastrado na tela de IA' },
      });
      setEditandoPreco(null);
      await recarregarConfig();
      // O custo já gravado NÃO é recalculado: aquelas chamadas foram medidas sem
      // preço, e reescrevê-las com a tabela de hoje seria inventar um número
      // para um gasto passado. O preço vale das próximas em diante.
      setAviso(`Preço de "${modelo}" cadastrado — vale das próximas chamadas em diante.`);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  };

  if (carregando && !config) {
    return (
      <div className="max-w-5xl mx-auto mt-10 flex items-center gap-3 text-gray-500 text-sm">
        <Loader2 className="animate-spin" size={18} /> Consultando a configuração de IA…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0">
            <Brain size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Inteligência Artificial</h2>
            <p className="text-sm text-gray-500">
              Chave, modelo e o custo real por site — medido em tokens, não estimado.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setCarregando(true);
            Promise.all([recarregarConfig(), recarregarCusto(periodo)])
              .catch((e) => setErro(mensagemDeErro(e)))
              .finally(() => setCarregando(false));
          }}
          className="text-sm text-gray-600 hover:text-black flex items-center gap-1.5 self-start"
        >
          <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <XCircle size={16} className="mt-0.5 shrink-0" />
          <span>{erro}</span>
        </div>
      )}
      {aviso && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <CheckCircle size={16} className="mt-0.5 shrink-0" />
          <span>{aviso}</span>
        </div>
      )}

      {/* ---------------------------------------------------------- chave -- */}
      <section className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center shrink-0">
            <KeyRound size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Chave do Gemini</h3>
            <p className="text-sm text-gray-500 mt-1">
              Gravada <strong>cifrada</strong> no banco e nunca devolvida ao navegador. Sem chave no
              banco, a API usa <code>GEMINI_API_KEY</code> do ambiente.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm bg-gray-50 border border-gray-200 rounded-lg p-5">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Em uso</div>
            <div className="font-semibold text-gray-900 mt-1">
              {config?.fonteChave === 'banco' && 'Chave do banco'}
              {config?.fonteChave === 'ambiente' && 'GEMINI_API_KEY do ambiente'}
              {config?.fonteChave === 'nenhuma' && <span className="text-red-600">Nenhuma</span>}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Chave no banco</div>
            <div className="font-mono text-gray-900 mt-1">
              {config?.chaveSufixo ? `••••••••${config.chaveSufixo}` : '— não há —'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Modelo</div>
            <div className="font-mono text-xs text-gray-900 mt-1">{config?.modelo}</div>
          </div>
        </div>

        {config && !config.podeGuardarChave && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 text-xs flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>Não dá para gravar a chave neste servidor:</strong> <code>CHAVE_CIFRA_DADOS</code>{' '}
              não está definida na API. Sem ela a chave iria ao banco em texto puro. Use{' '}
              <code>GEMINI_API_KEY</code> no ambiente enquanto isso.
            </span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              type={mostrarChave ? 'text' : 'password'}
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="Cole aqui a chave (aistudio.google.com/apikey)"
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black"
            />
            <button
              type="button"
              onClick={() => setMostrarChave((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label={mostrarChave ? 'Ocultar a chave' : 'Mostrar a chave'}
            >
              {mostrarChave ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button
            onClick={testar}
            disabled={testando}
            title="Faz uma geração de verdade — chave válida com faturamento desligado passa num teste de autenticação e falha aqui."
            className="border border-gray-300 hover:bg-gray-50 text-gray-800 px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {testando ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {chave.trim() ? 'Testar esta chave' : 'Testar a chave em uso'}
          </button>
          <button
            onClick={() => salvar({ chave: chave.trim() })}
            disabled={salvando || !chave.trim() || !config?.podeGuardarChave}
            className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {salvando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Salvar
          </button>
        </div>

        {config?.chaveSufixo && (
          <button
            onClick={() => salvar({ chave: '' })}
            disabled={salvando}
            className="text-xs text-gray-500 hover:text-red-600 underline underline-offset-2"
          >
            Apagar a chave do banco e voltar a usar a do ambiente
          </button>
        )}

        {diagnostico && (
          <div
            className={`rounded-lg p-4 text-sm border ${
              diagnostico.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-900'
            }`}
          >
            <div className="flex items-start gap-2 font-semibold">
              {diagnostico.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
              <span>{diagnostico.mensagem}</span>
            </div>
            <div className="mt-2 text-xs opacity-80 space-y-0.5 pl-6">
              <div>
                Modelo: <strong>{diagnostico.modelo}</strong>
                {diagnostico.statusHttp > 0 && ` · HTTP ${diagnostico.statusHttp}`} · {diagnostico.ms} ms
              </div>
              {diagnostico.resposta && <div>O modelo respondeu: “{diagnostico.resposta}”</div>}
              {diagnostico.consumo && (
                <div>
                  Custo deste teste: {diagnostico.consumo.tokensTotal} tokens ·{' '}
                  {diagnostico.consumo.precoConhecido
                    ? brl(diagnostico.consumo.custoBrl)
                    : 'preço deste modelo não cadastrado'}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* -------------------------------------------------------- modelos -- */}
      <section className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm space-y-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0">
            <Cpu size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">Modelos disponíveis</h3>
            <p className="text-sm text-gray-500 mt-1">
              Perguntados <strong>ao vivo</strong> ao provedor com a chave em uso. Do mais barato ao mais
              caro.
            </p>
          </div>
          {/*
            O preço vem do CATÁLOGO do Google, não da resposta da geração.

            A API do Gemini devolve token, nunca dinheiro — não existe custo na
            resposta de `generateContent`. Quem publica preço de forma
            consultável é a Cloud Billing Catalog API, e é o que este botão lê.
          */}
          <button
            onClick={() => void buscarPrecos()}
            disabled={buscandoPrecos}
            className="shrink-0 flex items-center gap-2 bg-white border border-gray-300 hover:border-gray-900 text-gray-900 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
          >
            {buscandoPrecos ? <Loader2 size={15} className="animate-spin" /> : <DownloadCloud size={15} />}
            Buscar preços no Google
          </button>
        </div>

        {/* ---------------------------------------- proposta do catálogo -- */}
        {catalogo && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <h4 className="text-sm font-bold text-gray-900 mr-auto">
                Preços publicados pelo Google
                <span className="ml-2 font-normal text-xs text-gray-500">
                  {catalogo.consultou
                    ? `${catalogo.propostas.length} modelo(s) casados · chave ${
                        catalogo.fonteChave === 'faturamento' ? 'de faturamento' : 'do Gemini'
                      }`
                    : 'não consultado'}
                </span>
              </h4>
              {catalogo.propostas.length > 0 && (
                <button
                  onClick={() => void aplicarPrecos()}
                  disabled={salvando || marcados.size === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
                >
                  Aplicar {marcados.size} preço{marcados.size === 1 ? '' : 's'}
                </button>
              )}
              <button
                onClick={() => setCatalogo(null)}
                className="text-xs text-gray-500 hover:text-black underline underline-offset-2"
              >
                fechar
              </button>
            </div>

            {catalogo.aviso && (
              <p className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-xs leading-relaxed">
                {catalogo.aviso}
              </p>
            )}

            {catalogo.propostas.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] text-gray-400 uppercase tracking-wide font-semibold border-b border-indigo-200">
                      <th className="pb-2 pr-3">Aplicar</th>
                      <th className="pb-2 pr-3">Modelo</th>
                      <th className="pb-2 pr-3 text-right">Cadastrado (ent/saí)</th>
                      <th className="pb-2 pr-3 text-right">Google (ent/saí)</th>
                      <th className="pb-2">SKU usado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100">
                    {catalogo.propostas.map((p) => (
                      <tr key={p.modelo} className={p.mudou ? '' : 'opacity-60'}>
                        <td className="py-2 pr-3">
                          <input
                            type="checkbox"
                            disabled={!p.aplicavel}
                            checked={marcados.has(p.modelo)}
                            onChange={(e) => {
                              const s = new Set(marcados);
                              if (e.target.checked) s.add(p.modelo);
                              else s.delete(p.modelo);
                              setMarcados(s);
                            }}
                          />
                        </td>
                        <td className="py-2 pr-3 font-mono font-semibold text-gray-900">{p.modelo}</td>
                        <td className="py-2 pr-3 text-right font-mono text-gray-500">
                          {p.atual
                            ? `${p.atual.usdEntradaMi.toFixed(3)} / ${p.atual.usdSaidaMi.toFixed(3)}`
                            : '— sem preço —'}
                        </td>
                        <td
                          className={`py-2 pr-3 text-right font-mono ${
                            p.mudou ? 'font-bold text-indigo-700' : 'text-gray-500'
                          }`}
                        >
                          {p.usdEntradaMi === null || p.usdSaidaMi === null
                            ? 'incompleto'
                            : `${p.usdEntradaMi.toFixed(3)} / ${p.usdSaidaMi.toFixed(3)}`}
                          {p.usdRaciocinioMi !== null && (
                            <span className="block text-[10px] font-normal text-gray-500">
                              raciocínio {p.usdRaciocinioMi.toFixed(3)}
                            </span>
                          )}
                        </td>
                        {/*
                          A descrição do SKU e o preço CRU ficam à vista.

                          A conversão para "por milhão" é a parte frágil disto: o
                          Google publica esses SKUs por token, e uma unidade que
                          o parser não reconheça daria um número errado com cara
                          de certo. Com o valor cru ao lado, o erro é visível
                          ANTES de gravar.
                        */}
                        <td className="py-2 text-[10px] leading-relaxed text-gray-500">
                          {p.skus.map((s) => (
                            <div key={`${s.papel}-${s.id}`}>
                              <span className="font-semibold uppercase">{s.papel}</span>: {s.descricao}{' '}
                              <span className="font-mono">
                                ({s.precoUnitario} / {s.unidade})
                              </span>
                              {s.variantes > 1 && (
                                <span className="text-amber-700"> · {s.variantes} variantes, usei a mais barata</span>
                              )}
                            </div>
                          ))}
                          {!p.aplicavel && (
                            <div className="text-amber-700 font-semibold">
                              o catálogo trouxe só um lado — não dá para aplicar
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {catalogo.ignorados.length > 0 && (
              <div>
                <button
                  onClick={() => setVerIgnorados(!verIgnorados)}
                  className="text-xs text-gray-500 hover:text-black underline underline-offset-2"
                >
                  {verIgnorados ? 'esconder' : 'ver'} {catalogo.ignorados.length} SKU(s) descartado(s)
                </button>
                {verIgnorados && (
                  <ul className="mt-2 space-y-1 text-[11px] text-gray-500 max-h-48 overflow-y-auto">
                    {catalogo.ignorados.map((i, k) => (
                      <li key={`${i.descricao}-${k}`}>
                        <span className="font-mono">{i.descricao}</span> — {i.motivo}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <p className="text-[11px] leading-relaxed text-gray-500 border-t border-indigo-200 pt-3">
              A API do Gemini devolve <strong>token, nunca dinheiro</strong> — não existe custo na
              resposta da geração. Isto aqui vem da Cloud Billing Catalog API
              {catalogo.servico && <> (<code className="font-mono">{catalogo.servico}</code>)</>}, e é
              gravado com a data de hoje e a origem na linha. O que já foi cobrado não é recalculado:
              preço novo vale das próximas chamadas em diante.
            </p>
          </div>
        )}

        {modelos && !modelos.consultouProvedor && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 text-sm flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{modelos.aviso}</span>
          </div>
        )}

        {modelos && modelos.modelos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wide font-semibold border-b border-gray-200">
                  <th className="pb-2 pr-4">Modelo</th>
                  <th className="pb-2 pr-4 text-right">US$/1M entrada</th>
                  <th className="pb-2 pr-4 text-right">US$/1M saída</th>
                  <th className="pb-2 pr-4">Preço conferido</th>
                  <th className="pb-2 text-right">Usar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modelos.modelos.map((m) => (
                  <tr key={m.id} className={m.emUso ? 'bg-indigo-50/50' : ''}>
                    <td className="py-2.5 pr-4">
                      <div className="font-mono text-xs font-semibold text-gray-900">{m.id}</div>
                      <div className="text-xs text-gray-500">{m.nome}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">
                      {m.preco ? m.preco.usdEntradaMi.toFixed(3) : '—'}
                    </td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">
                      {m.preco ? m.preco.usdSaidaMi.toFixed(3) : '—'}
                    </td>
                    <td className="py-2.5 pr-4 text-xs">
                      {m.preco ? (
                        <span
                          className={
                            diasDesde(m.preco.conferidoEm) > 90 ? 'text-amber-700 font-semibold' : 'text-gray-500'
                          }
                        >
                          {m.preco.conferidoEm}
                        </span>
                      ) : (
                        <span className="text-amber-700 font-semibold">sem preço cadastrado</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => {
                          setEditandoPreco(editandoPreco === m.id ? null : m.id);
                          setEntradaMi(m.preco ? String(m.preco.usdEntradaMi) : '');
                          setSaidaMi(m.preco ? String(m.preco.usdSaidaMi) : '');
                        }}
                        className="text-xs font-semibold text-gray-500 hover:text-black underline underline-offset-2 mr-3"
                      >
                        {m.preco ? 'preço' : 'cadastrar preço'}
                      </button>
                      {m.emUso ? (
                        <span className="text-xs font-bold text-indigo-600">EM USO</span>
                      ) : (
                        <button
                          onClick={() => salvar({ modelo: m.id })}
                          disabled={salvando}
                          className="text-xs font-semibold text-gray-600 hover:text-black underline underline-offset-2 disabled:opacity-40"
                        >
                          escolher
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {modelos.modelos
                  .filter((m) => m.id === editandoPreco)
                  .map((m) => (
                    <tr key={`${m.id}-preco`} className="bg-gray-50">
                      <td colSpan={5} className="p-4">
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">
                              US$ / 1M entrada
                            </label>
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={entradaMi}
                              onChange={(e) => setEntradaMi(e.target.value)}
                              className="w-32 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">
                              US$ / 1M saída
                            </label>
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={saidaMi}
                              onChange={(e) => setSaidaMi(e.target.value)}
                              className="w-32 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                            />
                          </div>
                          <button
                            onClick={() => void salvarPreco(m.id)}
                            disabled={salvando}
                            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
                          >
                            Salvar preço
                          </button>
                          <button
                            onClick={() => setEditandoPreco(null)}
                            className="text-xs text-gray-500 hover:text-black underline underline-offset-2 pb-2"
                          >
                            cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {modelos?.consultouProvedor && !modelos.modelos.some((m) => m.emUso) && (
          <div className="bg-red-50 border border-red-200 text-red-900 rounded-lg p-4 text-sm flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              O modelo configurado (<code>{config?.modelo}</code>) <strong>não aparece</strong> na lista desta
              chave. Escolha um da tabela acima.
            </span>
          </div>
        )}

        <div className="border-t border-gray-100 pt-5">
          <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-3">
            Modo da esteira da fábrica
          </div>
          <div className="flex gap-2">
            {(['ia', 'template'] as const).map((v) => (
              <button
                key={v}
                onClick={() => salvar({ modoGeracao: v })}
                disabled={salvando || config?.modoGeracao === v}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  config?.modoGeracao === v
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                }`}
              >
                {v === 'ia' ? 'IA (com queda para template)' : 'Somente template'}
              </button>
            ))}
          </div>
          {config && !config.cota && (
            <p className="text-xs text-amber-800 mt-3">
              Sem cota diária definida: a IA roda <strong>sem teto</strong>. Para limitar:{' '}
              <code>pnpm --filter @nyroh/api definir-cota gemini 5000</code>
            </p>
          )}
          {config?.cota && (
            <p className="text-xs text-gray-500 mt-3">
              Cota de hoje: {numero(config.cota.usadoHoje)} de {numero(config.cota.limiteDia)}
              {config.cota.pausado && <strong className="text-red-600"> · PAUSADA</strong>}
            </p>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------- custo -- */}
      <section className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center shrink-0">
              <BadgeDollarSign size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Custo por site</h3>
              <p className="text-sm text-gray-500 mt-1">Tokens reais reportados pelo provedor.</p>
            </div>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 self-start">
            {(Object.keys(ROTULO) as Periodo[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriodo(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                  periodo === p ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {ROTULO[p]}
              </button>
            ))}
          </div>
        </div>

        {resumo && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metrica rotulo="Gasto no período" valor={brl(resumo.custoBrl)} />
            <Metrica rotulo="Média por site" valor={brl(resumo.custoMedioPorSiteBrl)} />
            <Metrica rotulo="Sites atendidos" valor={numero(resumo.sitesAtendidos)} />
            <Metrica rotulo="Chamadas" valor={`${numero(resumo.chamadas)} (${numero(resumo.falhas)} falhas)`} />
          </div>
        )}

        {resumo && resumo.chamadasSemPreco > 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 text-sm flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>{numero(resumo.chamadasSemPreco)} chamada(s)</strong> usaram modelo sem preço
              cadastrado e não entram no total. O gasto é real; falta o preço para calculá-lo.
            </span>
          </div>
        )}

        {porSite.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center border-t border-gray-100">
            Nenhuma geração com IA no período.
          </div>
        ) : (
          <div className="overflow-x-auto border-t border-gray-100 pt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wide font-semibold">
                  <th className="pb-2 pr-4">Cliente</th>
                  <th className="pb-2 pr-4 text-right">Chamadas</th>
                  <th className="pb-2 pr-4 text-right">Tokens</th>
                  <th className="pb-2 text-right">Custo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {porSite.map((s) => (
                  <tr key={s.clienteId ?? s.clienteNome}>
                    <td className="py-2.5 pr-4">
                      <div className="font-semibold text-gray-900">{s.clienteNome}</div>
                      {s.clienteSlug && <div className="text-xs text-gray-400 font-mono">{s.clienteSlug}</div>}
                    </td>
                    <td className="py-2.5 pr-4 text-right">{numero(s.chamadas)}</td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">{numero(s.tokensTotal)}</td>
                    <td className="py-2.5 text-right font-semibold">{brl(s.custoBrl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const Metrica: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold">{rotulo}</div>
    <div className="text-lg font-bold text-gray-900 mt-1 tabular-nums">{valor}</div>
  </div>
);
